import React from 'react';
import * as Sentry from '@sentry/react';
/**
 * Sentry initialization for Maestro Connect.
 *
 * Design principles for beta:
 * - Production-only init (no dev noise, no Sentry on localhost)
 * - sendDefaultPii: false (do NOT send IP, cookies, headers by default)
 * - Session Replay: OFF (Maestro Connect serves minors; never record DOM)
 * - Performance tracing: OFF (no overengineering before PMF)
 * - Aggressive client-side PII scrubbing for emails, names, phone numbers,
 *   addresses, and student identifiers before any event leaves the browser
 *
 * Re-enable performance / replay only after a formal privacy review.
 */
// Public Sentry DSN — safe to embed client-side (designed for that).
const SENTRY_DSN = 'https://cec12e56749a6094979636d853596cab@o4511469919338496.ingest.us.sentry.io/4511469926744064';
// Field names that almost always contain PII. Case-insensitive substring match.
const PII_KEY_PATTERNS = ['email', 'name', 'phone', 'address', 'street', 'street2', 'city', 'state', 'zip', 'zipcode', 'postal', 'country', 'birth', 'dob', 'age', 'avatar', 'photo', 'profilepicture', 'password', 'token', 'secret', 'apikey', 'authorization', 'cookie', 'session', 'idtoken', 'refreshtoken', 'accesstoken', 'creditcard', 'cardnumber', 'cvv', 'ssn', 'student', 'studentid', 'parentname', 'parentemail', 'guardian', 'location', 'latitude', 'longitude', 'lat', 'lng'] as const;
// Regex patterns for scrubbing PII embedded inside free-form strings
// (e.g. error messages like "Failed to load user@example.com").
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
// Loose phone pattern: 7+ digits, optionally separated by space/dash/dot/paren
const PHONE_REGEX = /(?:\+?\d[\s.\-()]?){7,}\d/g;
const REDACTED = '[Redacted]';
/**
 * Returns true if a key name looks like it holds PII.
 */
function isPiiKey(key: string): boolean {
  const lower = key.toLowerCase();
  return PII_KEY_PATTERNS.some(p => lower.includes(p));
}
/**
 * Scrubs free-form strings: replaces any embedded emails and phone numbers.
 */
function scrubString(value: string): string {
  if (typeof value !== 'string') return value;
  return value.replace(EMAIL_REGEX, REDACTED).replace(PHONE_REGEX, REDACTED);
}
/**
 * Recursively scrubs PII from an arbitrary value (object, array, or primitive).
 * - Keys whose names match PII patterns have their values replaced with [Redacted].
 * - String values are passed through scrubString to catch inline emails/phones.
 * - Bounded depth to avoid pathological cycles or huge payloads.
 */
function scrubValue(value: unknown, depth = 0): unknown {
  if (depth > 8) return REDACTED;
  if (value == null) return value;
  if (typeof value === 'string') return scrubString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    return value.map(v => scrubValue(v, depth + 1));
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (isPiiKey(k)) {
        out[k] = REDACTED;
      } else {
        out[k] = scrubValue(v, depth + 1);
      }
    }
    return out;
  }
  return value;
}
/**
 * Scrub an Error/exception message in place.
 */
function scrubExceptionValues(exception: Sentry.ErrorEvent['exception']): Sentry.ErrorEvent['exception'] {
  if (!exception || !exception.values) return exception;
  exception.values = exception.values.map(ex => ({
    ...ex,
    value: ex.value ? scrubString(ex.value) : ex.value
  }));
  return exception;
}
/**
 * Safely detect whether we're running in a production build.
 *
 * `import.meta.env.PROD` is Vite-specific and throws at runtime in any
 * build environment that does not transform `import.meta.env` (Webpack/CRA,
 * esbuild without env config, plain browser ESM, Magic Patterns runtime).
 *
 * We try multiple signals in order, all wrapped in try/catch so that ANY
 * failure to read environment metadata results in `false` — meaning Sentry
 * silently does NOT initialize. This is the safest possible default: we
 * would rather lose monitoring than break the app or accidentally send
 * events from a non-production environment.
 */
function isProductionBuild(): boolean {
  try {
    // Vite-style: import.meta.env.PROD
    const meta = Function('return typeof import.meta !== "undefined" ? import.meta : undefined')() as any;
    if (meta && meta.env && meta.env.PROD === true) return true;
    // Webpack / CRA / Node-style fallback
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') {
      return true;
    }
  } catch {
    // Any access error → assume non-production
  }
  return false;
}
/**
 * Safely read the build-time release tag. Returns undefined if unavailable
 * in the current bundler environment.
 */
function getReleaseTag(): string | undefined {
  try {
    const meta = Function('return typeof import.meta !== "undefined" ? import.meta : undefined')() as any;
    if (meta && meta.env && typeof meta.env.VITE_SENTRY_RELEASE === 'string') {
      return meta.env.VITE_SENTRY_RELEASE;
    }
    if (typeof process !== 'undefined' && process.env && typeof process.env.VITE_SENTRY_RELEASE === 'string') {
      return process.env.VITE_SENTRY_RELEASE;
    }
  } catch {
    // Any access error → no release tag
  }
  return undefined;
}
/**
 * Initialize Sentry. Idempotent and no-op outside production.
 */
export function initSentry(): void {
  // Hard gate: only initialize in production builds. Any environment where
  // we cannot positively confirm production mode → skip init entirely.
  if (!isProductionBuild()) {
    return;
  }
  // Optional: build-time release tag for "errors per release" tracking.
  const release = getReleaseTag();
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: 'production',
    release,
    // Privacy posture for a platform serving minors:
    sendDefaultPii: false,
    // Performance / Tracing: OFF for beta. Re-enable post-PMF.
    tracesSampleRate: 0,
    // Session Replay: OFF. Do not record DOM on a platform with minor users.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Minimal default integrations only — explicitly omit BrowserTracing
    // and Replay so they cannot be enabled accidentally by defaults.
    integrations: [],
    // Last line of defense: scrub every outgoing event.
    beforeSend(event) {
      // Strip user identifiers entirely. For beta we do not need to know
      // which user errored — we only need the stack trace.
      delete event.user;
      // Strip request data that could contain query-string PII or cookies.
      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
        if (event.request.query_string) {
          event.request.query_string = REDACTED;
        }
        if (event.request.data) {
          event.request.data = scrubValue(event.request.data);
        }
        // Keep event.request.url (host + path) but scrub query/fragment.
        if (typeof event.request.url === 'string') {
          const [base] = event.request.url.split('?');
          event.request.url = base;
        }
      }
      // Scrub extra / contexts / tags — anywhere arbitrary data hides.
      if (event.extra) event.extra = scrubValue(event.extra) as typeof event.extra;
      if (event.contexts) {
        event.contexts = scrubValue(event.contexts) as typeof event.contexts;
      }
      if (event.tags) event.tags = scrubValue(event.tags) as typeof event.tags;
      // Scrub free-form text in exception messages.
      if (event.exception) {
        event.exception = scrubExceptionValues(event.exception);
      }
      // Scrub the top-level message field if present.
      if (typeof event.message === 'string') {
        event.message = scrubString(event.message);
      }
      return event;
    },
    // Scrub breadcrumbs (navigation, console, network) before they're queued.
    beforeBreadcrumb(breadcrumb) {
      if (!breadcrumb) return breadcrumb;
      // Strip query strings from navigation breadcrumbs.
      if (breadcrumb.data && typeof breadcrumb.data === 'object') {
        breadcrumb.data = scrubValue(breadcrumb.data) as typeof breadcrumb.data;
      }
      // Scrub free-form messages (e.g. console.log breadcrumbs).
      if (typeof breadcrumb.message === 'string') {
        breadcrumb.message = scrubString(breadcrumb.message);
      }
      // Drop user-input UI breadcrumbs entirely — they often capture typed
      // values (passwords, emails, names) from form fields.
      if (breadcrumb.category === 'ui.input') {
        return null;
      }
      return breadcrumb;
    }
  });
}
/**
 * Fallback UI shown by the Sentry ErrorBoundary when a render-time crash
 * escapes the React tree. Kept brand-neutral and minimal — works whether
 * Sentry is enabled or not (in dev it shows for any crash that bubbles up).
 */
function ErrorFallback({
  resetError
}: {
  resetError: () => void;
}) {
  return <div role="alert" style={{
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    textAlign: 'center',
    background: '#0F1626',
    color: '#ffffff',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  }} data-id="element-12574">
      <div style={{
      maxWidth: '480px'
    }} data-id="element-12575">
        <h1 style={{
        fontSize: '24px',
        fontWeight: 600,
        marginBottom: '12px'
      }} data-id="element-12576">
          Something went wrong
        </h1>
        <p style={{
        fontSize: '15px',
        lineHeight: 1.5,
        color: '#cbd5e1',
        marginBottom: '24px'
      }} data-id="element-12577">
          We've been notified and are looking into it. You can try again, or
          reload the page if the problem continues.
        </p>
        <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }} data-id="element-12578">
          <button type="button" onClick={resetError} style={{
          padding: '10px 18px',
          borderRadius: '8px',
          background: '#22c55e',
          color: '#ffffff',
          border: 'none',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer'
        }} data-id="element-12579">
            Try again
          </button>
          <button type="button" onClick={() => window.location.reload()} style={{
          padding: '10px 18px',
          borderRadius: '8px',
          background: 'transparent',
          color: '#ffffff',
          border: '1px solid #475569',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer'
        }} data-id="element-12580">
            Reload page
          </button>
        </div>
      </div>
    </div>;
}
/**
 * Top-level error boundary. Wrap <App /> in this in index.tsx.
 * - Catches render-time exceptions that escape lower boundaries
 * - Reports them to Sentry in production (via Sentry's wired-in onError)
 * - Shows a clean recovery UI in any environment
 */
export const SentryErrorBoundary = ({
  children
}: {
  children: React.ReactNode;
}) => <Sentry.ErrorBoundary fallback={({
  resetError
}) => <ErrorFallback resetError={resetError} data-id="element-12582" />} data-id="element-12581">
    {children}
  </Sentry.ErrorBoundary>;